import React from "react";
import ProductDetailLine from "./ProductDetailLine";
import { formatCurrency } from "@/lib/formatters";

interface GroupedProduct {
  id: number;
  nome: string;
  marca: string;
  brand_id: number;
  precoAluno2025: number;
  valorUnitario: number;
  qtdReferencia: number;
  quantidade: number;
  valorTotal: number;
}

interface BrandGroupProps {
  nome: string;
  produtos: GroupedProduct[];
  total: number;
  somaPrecoAluno2025: number;
  somaPrecoAluno2026: number;
  isCompactMode?: boolean;
}

const BrandGroup = ({
  nome,
  produtos,
  total,
  somaPrecoAluno2025,
  somaPrecoAluno2026,
  isCompactMode = false
}: BrandGroupProps) => {
  // Sort products by name, with special handling for Plataforma AZ
  const sortedProdutos = [...produtos].sort((a, b) => {
    // Special handling for Plataforma AZ products to be sequential
    const aPlatform = a.nome.includes("Plataforma AZ");
    const bPlatform = b.nome.includes("Plataforma AZ");

    if (aPlatform && bPlatform) {
      const aFirst = a.nome.includes("1º Semestre");
      const bFirst = b.nome.includes("1º Semestre");
      if (aFirst && !bFirst) return -1;
      if (!aFirst && bFirst) return 1;
    }

    // Sort by product name alphabetically
    return a.nome.localeCompare(b.nome);
  });

  return (
    <div className={`mb-${isCompactMode ? '3' : '6'}`}>
      <div className="bg-gray-50 border border-gray-200 rounded-lg overflow-hidden">
        {/* Brand Header */}
        <div className="bg-gray-100 px-3 py-2 border-b border-gray-200">
          <div className="flex justify-between items-center">
            <h4 className={`text-${isCompactMode ? 'base' : 'lg'} font-semibold text-gray-800`}>
              📚 {nome}
            </h4>
            <div className={`text-right ${isCompactMode ? 'text-sm' : 'text-sm'} text-gray-600 space-y-1`}>
              <div>
                <span className="font-medium">Total: </span>
                <span className="font-bold text-blue-600">{formatCurrency(total)}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Products Header */}
        <div className="bg-white px-3 py-2 border-b border-gray-200">
          <div className="flex justify-between items-center text-xs font-semibold text-gray-600 uppercase tracking-wide">
            <div className="flex-1">Produto</div>
            <div className="flex gap-4">
              <div className="min-w-[70px] text-center">Valor Unit.</div>
              <div className="min-w-[50px] text-center">Qtd.</div>
              <div className="min-w-[80px] text-center">Total</div>
            </div>
          </div>
        </div>

        {/* Products List */}
        <div className="bg-white">
          {sortedProdutos.map((produto, index) => (
            <div key={`product-${produto.id}-${index}`} className={`flex justify-between items-center py-${isCompactMode ? '2' : '3'} px-${isCompactMode ? '3' : '4'} border-b border-gray-100 hover:bg-gray-50`}>
              <div className="flex-1 mr-3">
                <div className={`text-${isCompactMode ? 'xs' : 'sm'} font-medium text-gray-900 leading-tight`}>
                  {produto.nome}
                </div>
                <div className={`${isCompactMode ? 'text-xs' : 'text-sm'} text-gray-600 space-x-4`}>
                  <span>
                    <span className="font-medium">Marca:</span> {produto.marca}
                  </span>
                  <span>
                    <span className="font-medium">Valor Unit.:</span> {formatCurrency(produto.valorUnitario)}
                  </span>
                  <span>
                    <span className="font-medium">Qtd.:</span> {produto.quantidade}
                  </span>
                  <span>
                    <span className="font-medium">Total:</span> 
                    <span className="font-bold text-blue-600 ml-1">{formatCurrency(produto.valorTotal)}</span>
                  </span>
                </div>
              </div>
              <div className={`flex items-center gap-${isCompactMode ? '4' : '6'} text-${isCompactMode ? 'xs' : 'sm'}`}>
                <div className="text-center min-w-[70px]">
                  <div className="font-semibold text-gray-900">
                    {formatCurrency(produto.valorUnitario)}
                  </div>
                </div>
                <div className="text-center min-w-[50px]">
                  <div className="font-semibold text-gray-900">
                    {produto.quantidade}
                  </div>
                </div>
                <div className="text-center min-w-[80px]">
                  <div className="font-bold text-blue-600">
                    {formatCurrency(produto.valorTotal)}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default BrandGroup;